import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api, resolveMediaUrl } from '../utils/api';
import { SocialLinksRow } from '../components/salon-browser';

export default function Branches() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const shopId = searchParams.get('shop_id');
  const [shop, setShop] = useState(null);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [locationDenied, setLocationDenied] = useState(false);

  useEffect(() => {
    if (!shopId) { setLoading(false); return; }
    let lat = null; let lng = null;
    const fetchData = async () => {
      setLoading(true);
      try { const shopData = await api.getShop(parseInt(shopId)); setShop(shopData); } catch { /* keep shop null */ }
      const fetchBranches = () => {
        api.getBranches(null, parseInt(shopId), lat, lng)
          .then(setBranches)
          .catch(() => setBranches([]))
          .finally(() => setLoading(false));
      };
      if (!navigator.geolocation) { fetchBranches(); return; }
      navigator.geolocation.getCurrentPosition(
        (pos) => { lat = pos.coords.latitude; lng = pos.coords.longitude; fetchBranches(); },
        () => { setLocationDenied(true); fetchBranches(); }
      );
    };
    fetchData();
  }, [shopId]);

  if (!shopId) {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--bg-base)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Roboto', sans-serif" }}>
        <p style={{ color: 'var(--text-muted)' }}>{t('branches.selectShopFirst')}</p>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-base)', color: '#ffffff', padding: '7rem 1.25rem 5rem', fontFamily: "'Roboto', sans-serif" }}>
      <div style={{ maxWidth: '1280px', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '2.5rem' }}>

        <header style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', alignItems: 'flex-start' }}>
          <Link
            to="/shops"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              fontFamily: "'Oswald', sans-serif",
              fontSize: '0.88rem',
              fontWeight: 600,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              color: '#ffffff',
              textDecoration: 'none',
              marginBottom: '1rem',
              transition: 'color 0.2s',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--orange)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = '#ffffff'; }}
          >
            ← {t('branches.backToShops')}
          </Link>

          <p style={{ fontFamily: "'Oswald', sans-serif", fontSize: '0.88rem', fontWeight: 600, letterSpacing: '0.2em', textTransform: 'uppercase', color: 'var(--orange)' }}>
            {t('branches.chooseLocation')}
          </p>
          <h1 style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 'clamp(3rem, 6vw, 4rem)', letterSpacing: '0.06em', color: '#ffffff', lineHeight: 1, margin: 0 }}>
            {shop ? t('branches.shopBranches', { name: shop.name }) : t('branches.branchesTitle')}
          </h1>
          <div style={{ width: '60px', height: '4px', background: '#ffffff', borderRadius: '2px', margin: '0.5rem 0' }} />
          
          {shop?.social_links && (
            <div style={{ marginTop: '0.5rem' }}>
              <SocialLinksRow social={shop.social_links} />
            </div>
          )}
          {locationDenied && (
            <p style={{ fontSize: '0.88rem', color: '#ffffff', marginTop: '0.5rem' }}>{t('branches.locationDenied')}</p>
          )}
        </header>

        {loading ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 1fr))', gap: '1.5rem' }}>
            {[1, 2, 3].map((i) => (
              <div key={i} style={{ height: '180px', borderRadius: '12px', background: 'var(--bg-card)', border: '1px solid var(--border)' }} />
            ))}
          </div>
        ) : branches.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '5rem 2rem', border: '1px solid var(--border-hover)', borderRadius: '12px', background: 'var(--bg-card)' }}>
            <p style={{ fontFamily: "'Oswald', sans-serif", fontSize: '1.2rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--orange)' }}>{t('branches.noBranchesFound')}</p>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 1fr))', gap: '1.5rem' }}>
            {branches.map((branch) => (
              <div
                key={branch.id}
                className="barber-card"
                style={{
                  display: 'flex',
                  gap: '1rem',
                  padding: '1rem',
                  borderRadius: '12px',
                  background: 'var(--bg-card)',
                  border: '1px solid var(--border)',
                  color: '#ffffff',
                  transition: 'all 0.2s',
                  alignItems: 'flex-start',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = 'var(--orange)';
                  e.currentTarget.style.boxShadow = 'var(--shadow-orange)';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = 'var(--border)';
                  e.currentTarget.style.boxShadow = 'none';
                  e.currentTarget.style.transform = 'none';
                }}
              >
                <div
                  style={{
                    width: '120px',
                    height: '120px',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    background: 'var(--bg-dark)',
                    border: '1px solid var(--border)',
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {branch.logo_url ? (
                    <img
                      src={resolveMediaUrl(branch.logo_url)}
                      alt=""
                      loading="lazy"
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  ) : (
                    <span style={{ fontSize: '2rem', opacity: 0.45 }} aria-hidden="true">📍</span>
                  )}
                </div>
                
                <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column', flex: 1, height: '100%' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
                    <span style={{ padding: '0.15rem 0.5rem', borderRadius: '100px', border: '1px solid var(--orange)', fontSize: '0.65rem', fontWeight: 700, fontFamily: "'Oswald', sans-serif", letterSpacing: '0.08em', textTransform: 'uppercase', color: '#ffffff' }}>
                      {(branch.shop?.category || shop?.category || 'Unisex').toUpperCase()}
                    </span>
                    {(branch.rating != null && branch.rating > 0) && (
                      <span style={{ padding: '0.15rem 0.5rem', borderRadius: '6px', background: '#1a1a1a', color: '#ffffff', fontSize: '0.75rem', fontWeight: 700 }}>
                        ★ {typeof branch.rating === 'number' ? branch.rating.toFixed(1) : branch.rating}
                      </span>
                    )}
                  </div>
                  
                  <h3 style={{ fontFamily: "'Oswald', sans-serif", fontSize: '1.2rem', fontWeight: 700, letterSpacing: '0.04em', margin: '0 0 0.25rem 0', color: '#ffffff' }}>
                    {branch.name}
                  </h3>
                  
                  {branch.address && (
                    <p style={{ fontFamily: "'Roboto', sans-serif", fontSize: '0.88rem', color: 'rgba(255, 255, 255, 0.88)', margin: '0 0 0.25rem 0', lineHeight: 1.4 }}>
                      {branch.address}
                    </p>
                  )}
                  
                  {branch.distance_km != null && (
                    <p style={{ fontFamily: "'Oswald', sans-serif", fontSize: '0.75rem', letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(255, 255, 255, 0.8)', margin: '0 0 0.25rem 0' }}>
                      {branch.distance_km.toFixed(1)} km away
                    </p>
                  )}
                  
                  {branch.working_hours && (
                    <p style={{ fontFamily: "'Roboto', sans-serif", fontSize: '0.75rem', color: '#ffffff', margin: '0 0 0.5rem 0' }}>
                      {t('shops.workingHours')}: {branch.working_hours}
                    </p>
                  )}
                  
                  <div style={{ marginTop: 'auto', paddingTop: '0.5rem' }}>
                    <Link
                      to={`/booking?branch_id=${branch.id}`}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '100%',
                        padding: '0.65rem 1rem',
                        borderRadius: '100px',
                        background: 'var(--orange)',
                        color: '#ffffff',
                        fontFamily: "'Oswald', sans-serif",
                        fontSize: '0.88rem',
                        fontWeight: 600,
                        letterSpacing: '0.06em',
                        textTransform: 'uppercase',
                        textDecoration: 'none',
                        transition: 'background 0.2s',
                      }}
                    >
                      {t('shops.bookNow')}
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
